import moment from "moment";
import type {
  ActivityType,
  CalendarConfig,
  Company,
  Configuration,
  Department,
  Device,
  Employee,
  FullEmployeeProfile,
  Kiosk,
  Project,
  SessionInformation,
  WorkPlace,
} from "@wkt/api/gwt/gwtDTO";
import { userApi, type SessionInfoParamsDTO } from "@wkt/api/user/userAPI";
import {
  clearKioskEmployeeId,
  getClientCompanyId,
  getDeviceUuid,
  getNativeDeviceUuid,
  setKioskEmployeeId,
  setClientCompanyId,
} from "@wkt/lib/auth-context";
import {
  getAppVersion,
  getNotificationToken,
  getRuntimeLocale,
  getRuntimePlatform,
  isAppRuntime,
} from "@wkt/lib/device";
import { initKeycloak, scheduleTokenRefresh } from "@wkt/lib/keycloak";
import { getEmployeeLocale, persistEmployeeLocale } from "@wkt/lib/splash-locale";
import {
  getBrowserLocale,
  mergeCustomLocalization,
  normalizeLocale,
  setLocale,
} from "@wkt/plugins/i18n";
import { defineStore } from "pinia";

type BridgeStatus = "idle" | "waiting" | "ready" | "error";
type SessionSource = "bootstrap" | null;
export type BootstrapOutcome =
  | "ready"
  | "kiosk"
  | "register"
  | "login-required"
  | "error";

export type BootstrapResult =
  | {
      outcome: "ready";
      sessionInformation: SessionInformation;
      source: SessionSource;
    }
  | {
      outcome: "kiosk" | "register";
      sessionInformation: SessionInformation | null;
      source: SessionSource;
    }
  | {
      outcome: "login-required";
    }
  | {
      outcome: "error";
      error: Error;
    };

interface SessionCatalogState {
  sessionInformation: SessionInformation | null;
  employee: Employee | null;
  company: Company | null;
  calendarConfig: CalendarConfig | null;
  kiosk: Kiosk | null;
  device: Device | null;
  configs: Configuration[];
  myGroup: Employee[];
  myProfiles: FullEmployeeProfile[];
  employeeProjects: Project[];
  employeeActivityTypes: ActivityType[];
  workplaces: WorkPlace[];
  departments: Department[];
  departmentsLoaded: boolean;
  departmentsHydrating: boolean;
  projects: Project[];
  bridgeStatus: BridgeStatus;
  hydrating: boolean;
  hydratedAt: string | null;
  lastError: string | null;
  sessionSource: SessionSource;
  bootstrapOutcome: BootstrapOutcome | null;
}

const BRIDGE_WAIT_INTERVAL_MS = 100;
const BRIDGE_WAIT_MAX_ATTEMPTS = 150;
const DEVICE_REGISTRATION_RETRY_INTERVAL_MS = 2000;
const DEVICE_REGISTRATION_MAX_ATTEMPTS = 10;

let deviceRegistrationRetryTimer: number | null = null;
let deviceRegistrationInFlight = false;
let lastRegisteredDeviceKey: string | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function createSessionInfoParams(): SessionInfoParamsDTO {
  const now = new Date();

  return {
    module: "CLIENT",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    date: {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    },
  };
}

function callApi<T>(
  runner: (
    onSuccess: (data: T) => void,
    onError: (error: Error) => void,
  ) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    runner(resolve, reject);
  });
}

interface SessionCatalogCollections {
  projects: Project[];
  workplaces: WorkPlace[];
}

function normalizeBootstrapError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(
    typeof error === "string" && error.trim().length > 0
      ? error
      : "Unable to bootstrap session",
  );
}

function resolveBootstrapOutcome(
  sessionInformation: SessionInformation | null,
): Extract<BootstrapOutcome, "ready" | "kiosk" | "register"> {
  if (
    sessionInformation?.employee &&
    sessionInformation.enabled !== false
  ) {
    return "ready";
  }

  if (sessionInformation?.kiosk) {
    return "kiosk";
  }

  return "register";
}

function clearDeviceRegistrationRetry(): void {
  if (deviceRegistrationRetryTimer == null || typeof window === "undefined") {
    return;
  }

  window.clearTimeout(deviceRegistrationRetryTimer);
  deviceRegistrationRetryTimer = null;
}

function shouldPersistEmployeeLocale(locale?: string | null): boolean {
  return typeof locale === "string" && locale.trim().length > 0;
}

async function loadSessionCollections(): Promise<SessionCatalogCollections> {
  const [workplaces, projects] = await Promise.all([
    callApi<WorkPlace[]>((onSuccess, onError) => {
      userApi.getWorkplaces(onSuccess, onError as (error: import("axios").AxiosError) => void);
    }),
    callApi<Project[]>((onSuccess, onError) => {
      userApi.getProjects(onSuccess, onError as (error: import("axios").AxiosError) => void);
    }),
  ]);

  return {
    workplaces: Array.isArray(workplaces) ? workplaces : [],
    projects: Array.isArray(projects) ? projects : [],
  };
}

async function loadDepartments(): Promise<Department[]> {
  const departments = await callApi<Department[]>((onSuccess, onError) => {
    userApi.getDepartments(onSuccess, onError as (error: import("axios").AxiosError) => void);
  });

  return Array.isArray(departments) ? departments : [];
}

export const useSessionCatalogStore = defineStore("sessionCatalog", {
  state: (): SessionCatalogState => ({
    sessionInformation: null,
    employee: null,
    company: null,
    calendarConfig: null,
    kiosk: null,
    device: null,
    configs: [],
    myGroup: [],
    myProfiles: [],
    employeeProjects: [],
    employeeActivityTypes: [],
    workplaces: [],
    departments: [],
    departmentsLoaded: false,
    departmentsHydrating: false,
    projects: [],
    bridgeStatus: "idle",
    hydrating: false,
    hydratedAt: null,
    lastError: null,
    sessionSource: null,
    bootstrapOutcome: null,
  }),

  getters: {
    isReady: (state) => state.bridgeStatus === "ready",
    isAdmin: (state) =>
      state.sessionInformation?.isAdmin === true ||
      (Array.isArray(state.sessionInformation?.profiles) &&
        state.sessionInformation.profiles.length > 0) ||
      state.sessionInformation?.adminProfile != null,
    getConfigValue: (state) => (key: string): string | null => {
      const config = state.configs.find((item) => item?.key === key);
      return typeof config?.value === "string" ? config.value : null;
    },
  },

  actions: {
    peekSessionInformation(): SessionInformation | null {
      return this.sessionInformation;
    },

    clearSession(options?: { preserveClientCompany?: boolean }): void {
      this.sessionInformation = null;
      this.employee = null;
      this.company = null;
      this.calendarConfig = null;
      this.kiosk = null;
      this.device = null;
      this.configs = [];
      this.myGroup = [];
      this.myProfiles = [];
      this.employeeProjects = [];
      this.employeeActivityTypes = [];
      this.workplaces = [];
      this.departments = [];
      this.departmentsLoaded = false;
      this.departmentsHydrating = false;
      this.projects = [];
      this.bridgeStatus = "idle";
      this.hydrating = false;
      this.hydratedAt = null;
      this.lastError = null;
      this.sessionSource = null;
      this.bootstrapOutcome = null;
      lastRegisteredDeviceKey = null;
      if (options?.preserveClientCompany !== true) {
        setClientCompanyId(null);
      }
      mergeCustomLocalization(null);
      clearDeviceRegistrationRetry();
    },

    applySessionInformation(
      sessionInformation: SessionInformation | null,
      source: SessionSource,
    ): BootstrapResult {
      if (!sessionInformation) {
        const outcome = "register";
        this.sessionInformation = null;
        this.employee = null;
        this.company = null;
        this.calendarConfig = null;
        this.kiosk = null;
        this.device = null;
        this.configs = [];
        this.myGroup = [];
        this.myProfiles = [];
        this.employeeProjects = [];
        this.employeeActivityTypes = [];
        this.workplaces = [];
        this.departments = [];
        this.departmentsLoaded = false;
        this.departmentsHydrating = false;
        this.projects = [];
        this.bridgeStatus = "idle";
        this.hydrating = false;
        this.hydratedAt = new Date().toISOString();
        this.lastError = null;
        this.sessionSource = source;
        this.bootstrapOutcome = outcome;

        return {
          outcome,
          sessionInformation,
          source,
        };
      }

      const outcome = resolveBootstrapOutcome(sessionInformation);
      this.sessionInformation = sessionInformation;
      this.employee = sessionInformation.employee ?? null;
      this.company = sessionInformation.company ?? null;
      this.calendarConfig = sessionInformation.calendar ?? null;
      this.kiosk = sessionInformation.kiosk ?? null;
      this.configs =
        (Array.isArray(sessionInformation.configList)
          ? sessionInformation.configList
          : this.configs) ?? [];
      this.myGroup = sessionInformation.myGroup ?? [];
      this.myProfiles = sessionInformation.empProfiles ?? [];
      this.employeeProjects = sessionInformation.employeeProjects ?? [];
      this.employeeActivityTypes =
        sessionInformation.employeeActivityType ?? [];
      this.bridgeStatus = outcome === "ready" ? "ready" : "idle";
      this.hydrating = false;
      this.hydratedAt = new Date().toISOString();
      this.lastError = null;
      this.sessionSource = source;
      this.bootstrapOutcome = outcome;

      if (sessionInformation.company?.id != null) {
        setClientCompanyId(sessionInformation.company.id);
      } else {
        setClientCompanyId(null);
      }

      return {
        outcome,
        sessionInformation,
        source,
      };
    },

    applySessionCollections(
      collections: Partial<SessionCatalogCollections> | null | undefined,
    ): void {
      if (!collections) {
        return;
      }

      this.workplaces = Array.isArray(collections.workplaces)
        ? collections.workplaces
        : [];
      this.projects = Array.isArray(collections.projects)
        ? collections.projects
        : [];
    },

    async ensureDepartmentsLoaded(force = false): Promise<boolean> {
      if (this.bootstrapOutcome !== "ready") {
        return false;
      }

      if (!force && this.departmentsLoaded) {
        return true;
      }

      if (this.departmentsHydrating) {
        for (
          let attempt = 0;
          attempt < BRIDGE_WAIT_MAX_ATTEMPTS;
          attempt += 1
        ) {
          if (!this.departmentsHydrating) {
            return this.departmentsLoaded;
          }

          await sleep(BRIDGE_WAIT_INTERVAL_MS);
        }

        return this.departmentsLoaded;
      }

      this.departmentsHydrating = true;

      try {
        this.departments = await loadDepartments();
        this.departmentsLoaded = true;
        return true;
      } catch (error) {
        console.warn("Unable to load departments for message editor", error);
        this.departments = [];
        this.departmentsLoaded = false;
        return false;
      } finally {
        this.departmentsHydrating = false;
      }
    },

    async bootstrapSession(force = false): Promise<BootstrapResult> {
      if (!force && this.sessionInformation) {
        return this.applySessionInformation(
          this.sessionInformation,
          this.sessionSource,
        );
      }

      if (this.hydrating) {
        for (
          let attempt = 0;
          attempt < BRIDGE_WAIT_MAX_ATTEMPTS;
          attempt += 1
        ) {
          if (!this.hydrating) {
            if (this.sessionInformation) {
              return this.applySessionInformation(
                this.sessionInformation,
                this.sessionSource,
              );
            }

            if (this.bootstrapOutcome === "login-required") {
              return { outcome: "login-required" };
            }

            return {
              outcome: "error",
              error: normalizeBootstrapError(this.lastError),
            };
          }

          await sleep(BRIDGE_WAIT_INTERVAL_MS);
        }

        if (this.sessionInformation) {
          return this.applySessionInformation(
            this.sessionInformation,
            this.sessionSource,
          );
        }

        if (this.bootstrapOutcome === "login-required") {
          return { outcome: "login-required" };
        }

        return {
          outcome: "error",
          error: normalizeBootstrapError(this.lastError),
        };
      }

      this.hydrating = true;
      this.bridgeStatus = "waiting";
      this.lastError = null;
      this.bootstrapOutcome = null;

      try {
        const authenticated = await initKeycloak({
          checkLoginIframe: false,
          onLoad: "check-sso",
          silentCheckSsoFallback: false,
        });

        if (!authenticated) {
          this.bridgeStatus = "idle";
          this.hydrating = false;
          this.lastError = "login-required";
          this.bootstrapOutcome = "login-required";
          return { outcome: "login-required" };
        }

        scheduleTokenRefresh();

        const sessionInformation = await callApi<SessionInformation>(
          (onSuccess, onError) => {
            userApi.getSessionInformation(
              createSessionInfoParams(),
              onSuccess,
              onError as (error: import("axios").AxiosError) => void,
            );
          },
        );

        if (!sessionInformation) {
          return this.applySessionInformation(null, "bootstrap");
        }

        const bootstrapResult = this.applySessionInformation(
          sessionInformation,
          "bootstrap",
        );

        this.departments = [];
        this.departmentsLoaded = false;
        this.departmentsHydrating = false;

        if (bootstrapResult.outcome === "ready") {
          try {
            this.applySessionCollections(await loadSessionCollections());
          } catch (error) {
            console.warn(
              "Unable to load session collections from Vue bootstrap; keeping Vue state fallback",
              error,
            );
            this.applySessionCollections({
              workplaces: this.workplaces,
              projects: this.projects,
            });
          }
        }

        this.applyRuntimeLocalization(sessionInformation);

        if (bootstrapResult.outcome === "ready") {
          void this.registerDevice();
        }

        return bootstrapResult;
      } catch (error) {
        this.bridgeStatus = "error";
        const bootstrapError = normalizeBootstrapError(error);
        this.lastError = bootstrapError.message;
        this.bootstrapOutcome = "error";
        return {
          outcome: "error",
          error: bootstrapError,
        };
      } finally {
        this.hydrating = false;
      }
    },

    async ensureSessionReady(): Promise<boolean> {
      const result = await this.bootstrapSession(false);
      return result.outcome === "ready";
    },

    async switchProfile(companyId: number): Promise<BootstrapResult> {
      if (!Number.isFinite(companyId)) {
        return {
          outcome: "error",
          error: new Error("Invalid company id for profile switch"),
        };
      }

      const targetCompanyId = Math.trunc(companyId);
      if (
        this.company?.id === targetCompanyId &&
        this.sessionInformation &&
        this.bridgeStatus === "ready"
      ) {
        return this.applySessionInformation(
          this.sessionInformation,
          this.sessionSource,
        );
      }

      const previousClientCompanyId = getClientCompanyId();
      const previousSessionInformation = this.sessionInformation;
      const previousSessionSource = this.sessionSource;
      const previousDevice = this.device;

      clearKioskEmployeeId();
      setClientCompanyId(targetCompanyId);

      const result = await this.bootstrapSession(true);
      const resolvedCompanyId =
        result.outcome === "ready" ||
        result.outcome === "kiosk" ||
        result.outcome === "register"
          ? (result.sessionInformation?.company?.id ?? null)
          : null;
      const resolvedDifferentCompany =
        resolvedCompanyId != null && resolvedCompanyId !== targetCompanyId;

      if (
        result.outcome === "error" ||
        result.outcome === "login-required" ||
        resolvedDifferentCompany
      ) {
        setClientCompanyId(previousClientCompanyId);
        clearKioskEmployeeId();

        if (previousSessionInformation) {
          this.applySessionInformation(
            previousSessionInformation,
            previousSessionSource,
          );
          this.device = previousDevice;
        } else {
          this.clearSession({ preserveClientCompany: true });
        }

        if (resolvedDifferentCompany) {
          return {
            outcome: "error",
            error: new Error("Profile switch resolved a different company"),
          };
        }
      }

      return result;
    },

    applyKioskSession(
      employeeId: number,
      sessionInformation: SessionInformation | null,
    ): BootstrapResult {
      if (
        !Number.isFinite(employeeId) ||
        !sessionInformation ||
        !sessionInformation.employee?.id ||
        sessionInformation.enabled !== true
      ) {
        return {
          outcome: "error",
          error: new Error("Invalid kiosk session payload"),
        };
      }

      setKioskEmployeeId(Math.trunc(employeeId));

      const result = this.applySessionInformation(
        sessionInformation,
        "bootstrap",
      );

      this.applyRuntimeLocalization(sessionInformation);

      if (result.outcome === "ready") {
        void this.registerDevice();
      }

      return result;
    },

    applyRuntimeLocalization(
      sessionInformation: SessionInformation | null,
    ): string {
      mergeCustomLocalization(
        sessionInformation?.company?.customLocalization ?? null,
      );

      const storedLocale = getEmployeeLocale();
      const employeeLocale = shouldPersistEmployeeLocale(
        sessionInformation?.employee?.locale,
      )
        ? sessionInformation?.employee?.locale
        : null;

      // El backend es la fuente de verdad. localStorage actúa como fallback
      // cuando el empleado aún no tiene locale persistido, principalmente para
      // que el splash previo a Vue aparezca en el idioma correcto.
      const targetLocale = normalizeLocale(
        employeeLocale ??
          storedLocale ??
          getRuntimeLocale() ??
          getBrowserLocale(),
      );

      setLocale(targetLocale);
      moment.locale(targetLocale);

      // Mantener localStorage alineado con backend para el splash previo a Vue.
      if (employeeLocale != null) {
        persistEmployeeLocale(targetLocale);
      }

      if (
        sessionInformation?.employee &&
        employeeLocale == null &&
        typeof sessionInformation.employee.id === "number"
      ) {
        const updatedEmployee = {
          ...sessionInformation.employee,
          locale: targetLocale,
        };

        sessionInformation.employee = updatedEmployee;
        this.employee = updatedEmployee;

        userApi.updateMyData(
          updatedEmployee,
          () => undefined,
          () => undefined,
        );
      }

      return targetLocale;
    },

    async registerDevice(attempt = 0): Promise<Device | null> {
      const notificationToken = getNotificationToken();
      const nativeDeviceUuid = getNativeDeviceUuid();
      const deviceUuid = nativeDeviceUuid ?? getDeviceUuid();
      const shouldWaitForNativeDeviceUuid = isAppRuntime() && !nativeDeviceUuid;

      if (!notificationToken || shouldWaitForNativeDeviceUuid) {
        if (
          !isAppRuntime() ||
          attempt >= DEVICE_REGISTRATION_MAX_ATTEMPTS ||
          typeof window === "undefined"
        ) {
          return null;
        }

        clearDeviceRegistrationRetry();
        deviceRegistrationRetryTimer = window.setTimeout(() => {
          deviceRegistrationRetryTimer = null;
          void this.registerDevice(attempt + 1);
        }, DEVICE_REGISTRATION_RETRY_INTERVAL_MS);
        return null;
      }

      clearDeviceRegistrationRetry();

      const deviceRegistrationKey = [
        this.company?.id ?? "",
        this.employee?.id ?? "",
        getRuntimePlatform(),
        deviceUuid ?? "",
        notificationToken,
        getAppVersion() ?? "",
      ].join("|");

      if (this.device && lastRegisteredDeviceKey === deviceRegistrationKey) {
        return this.device;
      }

      if (deviceRegistrationInFlight) {
        return this.device;
      }

      deviceRegistrationInFlight = true;

      try {
        const registeredDevice = await callApi<Device>((onSuccess, onError) => {
          userApi.registerDevice(
            {
              platform: getRuntimePlatform(),
              uuid: deviceUuid ?? undefined,
              token: notificationToken,
              nativeVersion: getAppVersion() ?? undefined,
            },
            onSuccess,
            onError as (error: import("axios").AxiosError) => void,
          );
        });

        this.device = registeredDevice ?? null;
        lastRegisteredDeviceKey = deviceRegistrationKey;
        return this.device;
      } catch (error) {
        if (
          isAppRuntime() &&
          attempt < DEVICE_REGISTRATION_MAX_ATTEMPTS &&
          typeof window !== "undefined"
        ) {
          clearDeviceRegistrationRetry();
          deviceRegistrationRetryTimer = window.setTimeout(() => {
            deviceRegistrationRetryTimer = null;
            void this.registerDevice(attempt + 1);
          }, DEVICE_REGISTRATION_RETRY_INTERVAL_MS);
        }

        console.warn("Unable to register device from Vue bootstrap", error);
        return null;
      } finally {
        deviceRegistrationInFlight = false;
      }
    },

  },
});
